import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RolesService } from '../../../../core/services/roles.service';
import { Permission } from '../../../../shared/models/permission';
import { CommonModule } from '@angular/common';
import { RoleUpsertRequest } from '../../../../shared/models/role';
import { showAlertDialog } from '../../../../shared/helpers/showAlertDialog';
import { AlertType } from '../../../../shared/models/alertType';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxPermissionsModule } from 'ngx-permissions';
import { PermissionsService } from '../../../../core/services/permissions.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-permissoes',
  imports: [FormsModule, CommonModule, MatTooltipModule, NgxPermissionsModule],
  templateUrl: './permissoes.component.html',
  styleUrl: './permissoes.component.scss'
})
export class PermissoesComponent {
  roleId?: number;
  role: RoleUpsertRequest = {
    id: 0,
    name: "",
    permissionsIds: []
  }

  permissoes?: Permission[];
  permissionsByCategory: Record<string, Permission[]> = {};
  categories: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private rolesService: RolesService,
    private permissionsService: PermissionsService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.roleId = Number(this.route.snapshot.paramMap.get('id'));

    this.permissionsService.getAll().subscribe((values) => {
      this.permissoes = values;
      this.groupPermissionsByCategory();
    });

    if (this.roleId) {
      this.role.id = this.roleId;

      this.rolesService.getRole(this.roleId).subscribe((value) => {
        this.role.name = value.name;
      })

      this.rolesService.getPermissionsRole(this.roleId).subscribe((values) => {
        this.role.permissionsIds = values.map(permission => permission.id);
      })
    }
  }

  groupPermissionsByCategory(): void {
    if (this.permissoes) {
      this.permissionsByCategory = this.permissoes.reduce((acc, permission) => {
        if (!acc[permission.category]) {
          acc[permission.category] = [];
        }
        acc[permission.category].push(permission);
        return acc;
      }, {} as Record<string, Permission[]>);

      this.categories = Object.keys(this.permissionsByCategory);
    }
  }

  areAllPermissionsInCategorySelected(category: string): boolean {
    const categoryPermissions = this.permissionsByCategory[category];
    return categoryPermissions.every(permission =>
      this.isPermissionSelected(permission)
    );
  }

  toggleAllInCategory(category: string): void {
    const categoryPermissions = this.permissionsByCategory[category];

    const allSelected = this.areAllPermissionsInCategorySelected(category);

    if (allSelected) {
      categoryPermissions.forEach(permission => {
        const index = this.role.permissionsIds.indexOf(permission.id);
        if (index !== -1) {
          this.role.permissionsIds.splice(index, 1);
        }
      });
    } else {
      categoryPermissions.forEach(permission => {
        if (!this.isPermissionSelected(permission)) {
          this.role.permissionsIds.push(permission.id);
        }
      });
    }
  }

  togglePermission(permission: Permission): void {
    const index = this.role.permissionsIds.indexOf(permission.id);
    if (index === -1) {
      this.role.permissionsIds.push(permission.id);
    } else {
      this.role.permissionsIds.splice(index, 1);
    }
  }

  getTooltipErrorMessage(): string {
    if (!this.role.name) {
      return "O nome do cargo é obrigatório.";
    }
    if (!this.role.permissionsIds.length) {
      return "Selecione ao menos uma permissão para salvar o cargo.";
    }
    return "";
  }

  isPermissionSelected(permission: Permission): boolean {
    return this.role.permissionsIds.includes(permission.id);
  }

  onDelete() {
    if (this.roleId) {
      showAlertDialog(this.dialog, AlertType.Warning, 'Atenção!', 'Você tem certeza que deseja inativar este cargo?')
        .afterClosed().subscribe((result: boolean) => {
          if (result && this.roleId) {
            this.rolesService.delete(this.roleId).subscribe({
              next: () => this.showAlert(AlertType.Success, 'Sucesso!', 'Cargo deletado com sucesso!'),
              error: () => this.showAlert(AlertType.Error, 'Ops!', 'Ocorreu um erro ao deletar o cargo. Tente novamente mais tarde.'),
            });
          }
        });
    }
  }

  onSubmit() {
    if (this.role.name && this.role.permissionsIds.length > 0) {
      let serviceCall;
      if (this.roleId) {
        serviceCall = this.rolesService.update(this.roleId, this.role);
      } else {
        serviceCall = this.rolesService.create(this.role);
      }
      const successMsg = this.roleId
        ? 'Cargo atualizado com sucesso!'
        : 'Cargo criado com sucesso!';
      const errorMsg = this.roleId
        ? 'Ocorreu um erro ao atualizar o cargo. Tente novamente mais tarde.'
        : 'Ocorreu um erro ao criar o cargo. Tente novamente mais tarde.';

      (serviceCall as Observable<any>).subscribe({
        next: () => this.showAlert(AlertType.Success, 'Sucesso!', successMsg),
        error: () => this.showAlert(AlertType.Error, 'Ops!', errorMsg),
      });
    }
  }

  private showAlert(type: AlertType, title: string, message: string) {
    showAlertDialog(this.dialog, type, title, message)
      .afterClosed()
      .subscribe(() => this.router.navigate(['cargos']));
  }

  close() {
    this.router.navigate(['cargos']);
  }

}
